import { codeDefault } from '@ui/lib/reactFlow/helpers'
import { useEffect, useMemo, useState } from 'react'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { a11yLight } from 'react-syntax-highlighter/dist/cjs/styles/hljs'
import { useShallow } from 'zustand/react/shallow'

import { NodeStep, NodeStepType } from '@ui/__generated__/graphql'
import { Button, Input, ToggleSwitch } from '@ui/components/core'
import { CloseIcon, PlusIcon } from '@ui/components/icons'
import { useNodeStepStore } from '@ui/stores/pipelinesWorkflow'
import { Config, DropStepConfig, RegexStepConfig, SampleStepConfig } from '@ui/stores/pipelinesWorkflow/NodeStepStore'
import { useQueryBuilderStore, type QueryState } from '@ui/stores/pipelinesWorkflow/QueryBuilderStore'
import { useDebounce } from '@uidotdev/usehooks'
import clsx from 'clsx'

import { StepProvider, useStepContext } from './StepProvider'

interface StepProps {
  step: NodeStep
  inputId: string
  queryConfig: QueryState
  openQueryBuilder: (stepId: string, inputId: string) => void
  openCodeEditor: () => void
  handleUpdateStepConfig: (stepId: string, config: Partial<Config>) => void
  handleSetSelectedStepOverlay: (show?: boolean) => void
}

export const Step = ({
  step,
  inputId,
  queryConfig,
  openQueryBuilder,
  openCodeEditor,
  handleUpdateStepConfig,
  handleSetSelectedStepOverlay,
}: StepProps) => {
  const { config, setNodeStepType, setStepId, setConfig } = useNodeStepStore(
    useShallow((state) => ({
      config: state.getConfig(step.id as string, step.stepType.type as NodeStepType),
      setConfig: state.setConfig,
      setNodeStepType: state.setNodeStepType,
      setStepId: state.setStepId,
    }))
  )
  const { setIds, stepId, updateQuery } = useQueryBuilderStore(
    useShallow((state) => ({ setIds: state.setIds, stepId: state.stepId, updateQuery: state.updateQuery }))
  )

  const predicateId = 'query-condition'
  const predicateValue = useMemo(() => {
    return queryConfig[predicateId]?.jsonataExpression ?? (step.predicate?.jsonata || '')
  }, [queryConfig, predicateId, step.predicate?.jsonata])

  // Set the initial query for the step
  useEffect(() => {
    setIds(step.id as string, predicateId)
    updateQuery(step.predicate?.ruleGroup || { combinator: 'and', rules: [] })

    return () => {
      setStepId('')
      setIds('', '')
    }
  }, [])

  return (
    <StepProvider
      step={step}
      inputId={inputId}
      queryConfig={queryConfig}
      config={config}
      setConfig={setConfig}
      openQueryBuilder={openQueryBuilder}
      openCodeEditor={openCodeEditor}
      setNodeStepType={setNodeStepType}
      setStepId={setStepId}
      handleUpdateStepConfig={handleUpdateStepConfig}
      handleSetSelectedStepOverlay={handleSetSelectedStepOverlay}>
      <div className={clsx('flex flex-col space-y-2 transition-all duration-300')}>
        {/* Code steps don't need a condition */}
        {step.stepType.type !== NodeStepType.Code && (
          <div
            className={clsx('transition-all duration-300', {
              'rounded-md border-2 border-gray-300 bg-gray-200 p-0.5': stepId === step.id,
            })}>
            <Input
              id={predicateId}
              label="Condition"
              placeholder="attribute.name == 'value'"
              isRequired
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleSetSelectedStepOverlay(true)
                setNodeStepType(NodeStepType.Filter)
                openQueryBuilder(step.id as string, predicateId)
              }}
              value={predicateValue}
              readOnly
            />
          </div>
        )}
        <StepTypeInputs />
      </div>
    </StepProvider>
  )
}

const StepTypeInputs = () => {
  const { step, config, handleUpdateStepConfig } = useStepContext()

  const debouncedLocalConfig = useDebounce(config, 750)

  useEffect(() => {
    // only update store config on debounce to reduce excessive re-renders elsewhere
    handleUpdateStepConfig(step.id, debouncedLocalConfig)
  }, [debouncedLocalConfig])

  switch (step.stepType.type) {
    case NodeStepType.Drop:
      return <DropStepInputs />
    case NodeStepType.Regex:
      return <RegexStepInputs />
    case NodeStepType.Code:
      return <CodeStepInputs />
    case NodeStepType.Sample:
      return <SampleStepInputs />
    default:
      return null
  }
}

const CodeStepInputs = () => {
  const { setConfig, step, openCodeEditor, setStepId } = useStepContext()

  const [loadingCode, setLoadingCode] = useState(true)
  const [code, setCode] = useState(step.config.code ?? codeDefault)

  // Initialize the code step config
  useEffect(() => {
    setConfig(step.id as string, NodeStepType.Code, { code })
  }, [step.id])

  useEffect(() => {
    setCode(step.config.code ?? codeDefault)
  }, [step.config.code])

  // We need to wait for the code to load before displaying it
  // otherwise the code will flash on the screen for a split second
  setTimeout(() => {
    setLoadingCode(false)
  }, 1000)

  return (
    <>
      <div
        className="group cursor-pointer space-y-2"
        onClick={() => {
          setStepId(step.id as string)
          openCodeEditor()
        }}>
        <Button variant="primary" size="small" className="w-full" onClick={openCodeEditor}>
          Edit code
        </Button>

        <SyntaxHighlighter
          style={a11yLight}
          wrapLongLines
          language="javascript"
          className={clsx(
            'h-40 max-h-40 min-h-20 min-w-full rounded border-2 border-gray-300 bg-white p-2 text-xs transition-all duration-300',
            'group-hover:shadow-md'
          )}>
          {loadingCode ? 'Loading...' : code}
        </SyntaxHighlighter>
      </div>
    </>
  )
}

const DropStepInputs = () => {
  const { config: dropStepConfig, setConfig, step } = useStepContext()
  const config = dropStepConfig as DropStepConfig

  const addField = () => {
    setConfig(step.id as string, NodeStepType.Drop, {
      ...config,
      dropFields: [...(config.dropFields || []), ''],
    })
  }

  const removeField = (index: number) => {
    setConfig(step.id as string, NodeStepType.Drop, {
      ...config,
      dropFields: config.dropFields?.filter((_, i) => i !== index),
    })
  }

  const removeAllFields = () => {
    setConfig(step.id as string, NodeStepType.Drop, { ...config, dropFields: [] })
  }

  const updateField = (index: number, value: string) => {
    setConfig(step.id as string, NodeStepType.Drop, {
      ...config,
      dropFields: (config.dropFields || []).map((field, i) => (i === index ? value : field)),
    })
  }

  const { dropEntireEvent, dropFields = [] } = config

  const isDropFieldsDisabled = Boolean(dropEntireEvent)

  return (
    <>
      <div className="flex w-full items-center">
        <div className="flex w-full flex-row items-center justify-between space-x-2 py-1">
          <p className="text-xs font-semibold">Drop Entire Event</p>
          <ToggleSwitch
            onChange={(dropEntireEvent) =>
              setConfig(step.id as string, NodeStepType.Drop, { ...config, dropEntireEvent })
            }
            checked={config.dropEntireEvent}
          />
        </div>
      </div>
      {!isDropFieldsDisabled && (
        <div
          className={clsx(
            { ['opacity-50']: isDropFieldsDisabled },
            'flex w-full flex-col items-end space-y-2 rounded border border-gray-300 p-1.5 transition-all duration-300'
          )}>
          <div className="flex w-full flex-row justify-between">
            <p className="text-xs font-semibold">Drop Fields</p>

            <div
              className="flex size-6 cursor-pointer items-center justify-center rounded transition-all duration-300 ease-in-out hover:bg-gray-200"
              onClick={addField}>
              <PlusIcon className="size-4 cursor-pointer stroke-gray-900 hover:scale-110" />
            </div>
          </div>
          <div className="flex w-full flex-col items-end space-y-2">
            {dropFields.map((_, index) => (
              <div className="flex w-full flex-row items-end space-x-2" key={index}>
                <div className="w-full">
                  <Input
                    label="Drop Field"
                    placeholder="resource.service"
                    id={`drop-field-${index}`}
                    isRequired
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newField = e.target.value
                      updateField(index, newField)
                    }}
                    value={dropFields[index]}
                    disabled={isDropFieldsDisabled}
                  />
                </div>

                <div className="group h-full pb-2.5 hover:cursor-pointer" onClick={() => removeField(index)}>
                  <CloseIcon className="size-2.5 stroke-gray-500 transition-all duration-300 hover:scale-110" />
                </div>
              </div>
            ))}

            {dropFields.length >= 1 && (
              <Button
                variant="primary-red"
                size="small"
                onClick={removeAllFields}
                disabled={isDropFieldsDisabled}
                className="w-full">
                <p>Remove All Fields</p>
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

const RegexStepInputs = () => {
  const { step, openQueryBuilder, setConfig, config: regexStepConfig } = useStepContext()
  const config = regexStepConfig as RegexStepConfig

  const { setIds, updateQuery } = useQueryBuilderStore(
    useShallow((state) => ({ setIds: state.setIds, updateQuery: state.updateQuery }))
  )

  useEffect(() => {
    // Set the field id and update the field
    setIds(step.id as string, 'regex-field')
    updateQuery(step.predicate?.ruleGroup || { combinator: 'and', rules: [] })

    // Set the pattern id and update the pattern
    setIds(step.id as string, 'regex-pattern')
    updateQuery(step.predicate?.ruleGroup || { combinator: 'and', rules: [] })

    return () => {
      setIds('', '')
    }
  }, [])

  return (
    <>
      <Input
        id="regex-field"
        label="Field"
        placeholder="resource.service"
        isRequired
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const field = e.target.value
          setConfig(step.id as string, NodeStepType.Regex, { ...config, field })
        }}
        value={config.field}
      />

      <Input
        id="regex-pattern"
        label="RegEx"
        placeholder="rege(x(es)?|xps?)"
        isRequired
        onClick={() => {
          openQueryBuilder(step.id as string, 'regex-field')
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const regex = e.target.value
          setConfig(step.id as string, NodeStepType.Regex, { ...config, regex })
        }}
        value={config.regex}
      />

      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold">Keep Match</p>
        <ToggleSwitch
          onChange={(keepMatch) => setConfig(step.id as string, NodeStepType.Regex, { ...config, keepMatch })}
          checked={config.keepMatch}
        />
      </div>
    </>
  )
}

const SampleStepInputs = () => {
  const { step, setConfig, config: sampleStepConfig } = useStepContext()
  const config = sampleStepConfig as SampleStepConfig

  return (
    <>
      <Input
        label="Retain (%)"
        placeholder="resource.service"
        isRequired
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const samplePercentage = Number(e.target.value)
          if (samplePercentage >= 0 && samplePercentage <= 100)
            setConfig(step.id as string, NodeStepType.Sample, { ...config, samplePercentage })
        }}
        value={config.samplePercentage}
        type="number"
      />
    </>
  )
}
